class NookPreorder extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.product-details');
    if (!this.scope || this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-nm-open]');
    this.variants = this.readJSON(this.querySelector('[data-nm-variants]')?.textContent, []);
    this.variantId = this.dataset.variantId;
    this.opener.addEventListener('click', () => {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }, options);
    this.querySelector('[data-nm-close]').addEventListener('click', () => this.dialog.close(), options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    }, options);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.dialog.close();
    }, options);
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dialog.close();
    }, options);
    this.dialog.addEventListener('close', () => {
      this.restoreScroll();
      if (!this.hidden) this.opener.focus({ preventScroll: true });
    }, options);
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener('variant:update', (event) => {
      const detail = event.detail;
      if (String(detail?.data?.productId) !== this.dataset.productId) return;
      const updated = detail.data.html?.querySelector(`nm-preorder[data-product-id="${this.dataset.productId}"] [data-nm-variants]`);
      if (updated) this.variants = this.readJSON(updated.textContent, this.variants);
      this.variantId = String(detail.resource?.id ?? '');
      this.refresh();
    }, options);
    this.scope.addEventListener('change', (event) => {
      if (event.target.matches('input[name="id"]')) {
        this.variantId = event.target.value;
        this.refresh();
      }
    }, options);
    document.addEventListener('essential-preorder:cart-updated', () => this.refresh(), options);
    this.attachEmbed();
    if (!this.embed) {
      this.discovery = new MutationObserver(() => {
        this.attachEmbed();
        if (this.embed) this.discovery.disconnect();
      });
      this.discovery.observe(document.body, { childList: true, subtree: true });
    }
    this.refresh();
  }

  disconnectedCallback() {
    this.restoreScroll();
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.discovery?.disconnect();
    this.scope?.classList.remove('nm-preorder-active');
  }

  restoreScroll() {
    if (this.previousOverflow === undefined) return;
    document.documentElement.style.overflow = this.previousOverflow;
    this.previousOverflow = undefined;
  }

  readJSON(value, fallback) {
    try { return JSON.parse(value) ?? fallback; } catch { return fallback; }
  }

  attachEmbed() {
    if (this.embed) return;
    this.embed = document.querySelector('essential-preorder-app-embed');
    if (!this.embed) return;
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(this.embed, { attributes: true, attributeFilter: ['preorder-app-data', 'preorder-app-data-inventory-quantity-limits', 'preorder-app-data-market-quantity', 'inventory-quantity-by-variant-id'] });
    this.refresh();
  }

  getState() {
    const variant = this.variants.find((item) => String(item.id) === String(this.variantId));
    if (!variant?.available || !this.embed) return null;
    const data = this.readJSON(this.embed.getAttribute('preorder-app-data'), {});
    const config = window.essentialPreorderConfigs ?? {};
    const marketId = String(config.selectedB2bMarketId || config.selectedMarketId || '');
    const matchesVariant = (campaign) => campaign.placement?.specificProducts?.some((id) => String(id) === String(variant.id)) || campaign.sellingPlanIds?.some((id) => variant.selling_plan_ids.some((plan) => String(plan) === String(id)));
    const campaigns = (data.preorderCampaigns ?? []).filter(matchesVariant);
    const campaign = campaigns.find((item) => item.content.marketType === 'SPECIFIC' && item.content.specificMarkets?.some((market) => String(market.shopifyId).split('/').pop() === marketId)) ?? campaigns.find((item) => !item.content.marketType || item.content.marketType === 'ALL');
    if (!campaign) return null;
    const limits = this.readJSON(this.embed.getAttribute('preorder-app-data-inventory-quantity-limits'), []);
    const limit = limits.find((item) => String(item.variantId) === String(variant.id))?.inventoryAvailable ?? campaign.content.inventoryAvailable;
    const market = this.readJSON(this.embed.getAttribute('preorder-app-data-market-quantity'), {});
    const quantities = { ...this.readJSON(this.embed.getAttribute('inventory-quantity-by-variant-id'), {}), ...market.total, ...market[config.selectedMarketId], ...market[config.selectedB2bMarketId] };
    const quantity = Number(quantities[variant.id] ?? variant.inventory_quantity);
    if (campaign.content.preorderType === 'OUT_OF_STOCK' && (variant.inventory_management !== 'shopify' || quantity > 0 || variant.inventory_policy !== 'continue')) return null;
    if (campaign.content.preorderType === 'IN_STOCK' && quantity <= 0) return null;
    let remaining = null;
    if (campaign.content.preorderType === 'IN_STOCK') remaining = Math.max(0, quantity);
    else if (campaign.content.preorderType !== 'ALWAYS' && limit !== null && limit !== undefined && Number.isFinite(Number(limit))) remaining = Math.max(0, Number(limit) - Math.abs(Number(quantities[variant.id] ?? 0)));
    if (remaining === 0) return null;
    return { campaign, remaining };
  }

  setDate(element, message) {
    element.replaceChildren();
    const date = message.match(/\b\d{1,2}\s+[\p{L}.]+\s+\d{4}\b/u);
    if (!date) { element.textContent = message; return; }
    const strong = document.createElement('strong');
    strong.textContent = date[0];
    element.append(message.slice(0, date.index), strong, message.slice(date.index + date[0].length));
  }

  refresh() {
    const state = this.getState();
    this.hidden = !state;
    this.scope.classList.toggle('nm-preorder-active', Boolean(state));
    if (!state) {
      if (this.dialog.open) this.dialog.close();
      return;
    }
    const message = (state.campaign.content.message ?? '').replace(/<[^>]*>/g, '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
    this.querySelector('[data-nm-date-row]').hidden = !message;
    this.querySelector('[data-nm-dialog-date-row]').hidden = !message;
    this.setDate(this.querySelector('[data-nm-date]'), message);
    this.setDate(this.querySelector('[data-nm-dialog-date]'), message);
    const payment = this.querySelector('[data-nm-payment]');
    payment.textContent = state.campaign.content.paymentType === 'FULL' ? payment.dataset.full : payment.dataset.partial;
  }
}

if (!customElements.get('nm-preorder')) customElements.define('nm-preorder', NookPreorder);
